import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import {
	HomeIcon,
	MegaphoneIcon,
	CpuChipIcon,
	PhotoIcon,
	Cog6ToothIcon,
	UserIcon,
	Bars3Icon,
	XMarkIcon,
	ArrowRightOnRectangleIcon,
	BellIcon,
	ChevronDownIcon
} from '@heroicons/react/24/outline';
import { Menu, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import clsx from 'clsx';

const navigation = [
	{ name: '仪表板', href: '/dashboard', icon: HomeIcon },
	{ name: '广告系列', href: '/campaigns', icon: MegaphoneIcon },
	{ name: 'Facebook 账户', href: '/facebook-accounts', icon: UserIcon },
	{ name: 'AI 设置', href: '/ai-settings', icon: CpuChipIcon },
	{ name: '素材上传', href: '/upload', icon: PhotoIcon },
];

function Layout() {
	const [sidebarOpen, setSidebarOpen] = useState(false);
	const location = useLocation();
	const navigate = useNavigate();
	const { user, logout } = useAuthStore();

	const handleLogout = async () => {
		await logout();
		navigate('/login');
	};

	return (
		<div className="min-h-screen bg-gray-50">
			{/* 移动端侧边栏 */}
			<Transition.Root show={sidebarOpen} as={Fragment}>
				<div className="relative z-50 lg:hidden">
					<Transition.Child
						as={Fragment}
						enter="transition-opacity ease-linear duration-300"
						enterFrom="opacity-0"
						enterTo="opacity-100"
						leave="transition-opacity ease-linear duration-300"
						leaveFrom="opacity-100"
						leaveTo="opacity-0"
					>
						<div className="fixed inset-0 bg-gray-900/80" />
					</Transition.Child>

					<div className="fixed inset-0 flex">
						<Transition.Child
							as={Fragment}
							enter="transition ease-in-out duration-300 transform"
							enterFrom="-translate-x-full"
							enterTo="translate-x-0"
							leave="transition ease-in-out duration-300 transform"
							leaveFrom="translate-x-0"
							leaveTo="-translate-x-full"
						>
							<div className="relative mr-16 flex w-full max-w-xs flex-1">
								<Transition.Child
									as={Fragment}
									enter="ease-in-out duration-300"
									enterFrom="opacity-0"
									enterTo="opacity-100"
									leave="ease-in-out duration-300"
									leaveFrom="opacity-100"
									leaveTo="opacity-0"
								>
									<div className="absolute left-full top-0 flex w-16 justify-center pt-5">
										<button
											type="button"
											className="-m-2.5 p-2.5"
											onClick={() => setSidebarOpen(false)}
										>
											<span className="sr-only">关闭侧边栏</span>
											<XMarkIcon className="h-6 w-6 text-white" aria-hidden="true" />
										</button>
									</div>
								</Transition.Child>
								<div className="flex grow flex-col gap-y-5 overflow-y-auto bg-white px-6 pb-2">
									<div className="flex h-16 shrink-0 items-center">
										<h1 className="text-xl font-bold text-gradient">
											Facebook AI 广告
										</h1>
									</div>
									<nav className="flex flex-1 flex-col">
										<ul role="list" className="flex flex-1 flex-col gap-y-7">
											<li>
												<ul role="list" className="-mx-2 space-y-1">
													{navigation.map((item) => (
														<li key={item.name}>
															<Link
																to={item.href}
																className={clsx(
																	location.pathname === item.href
																		? 'bg-gray-50 text-primary-600'
																		: 'text-gray-700 hover:text-primary-600 hover:bg-gray-50',
																	'group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold'
																)}
																onClick={() => setSidebarOpen(false)}
															>
																<item.icon
																	className={clsx(
																		location.pathname === item.href
																			? 'text-primary-600'
																			: 'text-gray-400 group-hover:text-primary-600',
																		'h-6 w-6 shrink-0'
																	)}
																	aria-hidden="true"
																/>
																{item.name}
															</Link>
														</li>
													))}
												</ul>
											</li>
										</ul>
									</nav>
								</div>
							</div>
						</Transition.Child>
					</div>
				</div>
			</Transition.Root>

			{/* 桌面端侧边栏 */}
			<div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col">
				<div className="flex grow flex-col gap-y-5 overflow-y-auto border-r border-gray-200 bg-white px-6">
					<div className="flex h-16 shrink-0 items-center">
						<h1 className="text-xl font-bold text-gradient">
							Facebook AI 广告优化系统
						</h1>
					</div>
					<nav className="flex flex-1 flex-col">
						<ul role="list" className="flex flex-1 flex-col gap-y-7">
							<li>
								<ul role="list" className="-mx-2 space-y-1">
									{navigation.map((item) => (
										<li key={item.name}>
											<Link
												to={item.href}
												className={clsx(
													location.pathname === item.href
														? 'bg-gray-50 text-primary-600'
														: 'text-gray-700 hover:text-primary-600 hover:bg-gray-50',
													'group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold'
												)}
											>
												<item.icon
													className={clsx(
														location.pathname === item.href
															? 'text-primary-600'
															: 'text-gray-400 group-hover:text-primary-600',
														'h-6 w-6 shrink-0'
													)}
													aria-hidden="true"
												/>
												{item.name}
											</Link>
										</li>
									))}
								</ul>
							</li>
						</ul>
					</nav>
				</div>
			</div>

			{/* 主内容区域 */}
			<div className="lg:pl-72">
				{/* 顶部导航栏 */}
				<div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8">
					<button
						type="button"
						className="-m-2.5 p-2.5 text-gray-700 lg:hidden"
						onClick={() => setSidebarOpen(true)}
					>
						<span className="sr-only">打开侧边栏</span>
						<Bars3Icon className="h-6 w-6" aria-hidden="true" />
					</button>

					{/* 分隔线 */}
					<div className="h-6 w-px bg-gray-200 lg:hidden" aria-hidden="true" />

					<div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
						<div className="flex flex-1"></div>
						<div className="flex items-center gap-x-4 lg:gap-x-6">
							{/* 通知按钮 */}
							<button
								type="button"
								className="-m-2.5 p-2.5 text-gray-400 hover:text-gray-500"
							>
								<span className="sr-only">查看通知</span>
								<BellIcon className="h-6 w-6" aria-hidden="true" />
							</button>

							{/* 分隔线 */}
							<div
								className="hidden lg:block lg:h-6 lg:w-px lg:bg-gray-200"
								aria-hidden="true"
							/>

							{/* 用户菜单 */}
							<Menu as="div" className="relative">
								<Menu.Button className="-m-1.5 flex items-center p-1.5">
									<span className="sr-only">打开用户菜单</span>
									<div className="h-8 w-8 rounded-full bg-primary-500 flex items-center justify-center">
										<span className="text-sm font-medium text-white">
											{user?.username?.charAt(0)?.toUpperCase() || 'U'}
										</span>
									</div>
									<span className="hidden lg:flex lg:items-center">
										<span
											className="ml-4 text-sm font-semibold leading-6 text-gray-900"
											aria-hidden="true"
										>
											{user?.username || '用户'}
										</span>
										<ChevronDownIcon
											className="ml-2 h-5 w-5 text-gray-400"
											aria-hidden="true"
										/>
									</span>
								</Menu.Button>
								<Transition
									as={Fragment}
									enter="transition ease-out duration-100"
									enterFrom="transform opacity-0 scale-95"
									enterTo="transform opacity-100 scale-100"
									leave="transition ease-in duration-75"
									leaveFrom="transform opacity-100 scale-100"
									leaveTo="transform opacity-0 scale-95"
								>
									<Menu.Items className="absolute right-0 z-10 mt-2.5 w-32 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none">
										<Menu.Item>
											{({ active }) => (
												<Link
													to="/profile"
													className={clsx(
														active ? 'bg-gray-50' : '',
														'block px-3 py-1 text-sm leading-6 text-gray-900'
													)}
												>
													个人资料
												</Link>
											)}
										</Menu.Item>
										<Menu.Item>
											{({ active }) => (
												<button
													onClick={handleLogout}
													className={clsx(
														active ? 'bg-gray-50' : '',
														'block w-full text-left px-3 py-1 text-sm leading-6 text-gray-900'
													)}
												>
													退出登录
												</button>
											)}
										</Menu.Item>
									</Menu.Items>
								</Transition>
							</Menu>
						</div>
					</div>
				</div>

				{/* 页面内容 */}
				<main className="py-10">
					<div className="px-4 sm:px-6 lg:px-8">
						<Outlet />
					</div>
				</main>
			</div>
		</div>
	);
}

export default Layout;
